import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";

import { CoachListGrid } from "~/components/coach-list-grid";
import { FaqList } from "~/components/faq-list";
import { BottomCTA } from "~/components/feature/bottom-cta";
import { BreakdownIntro } from "~/components/feature/breakdown-intro";
import { MidListCTA } from "~/components/feature/mid-list-cta";
import { MarkdownContent } from "~/components/markdown-content";
import { Heading } from "~/components/ui/heading";
import { getSubcategoryPageContent } from "~/lib/functions/coach-group-seo";
import { getSubcategoryBySlug } from "~/lib/functions/expertise";
import { coachQueryOptions } from "~/lib/query-options";
import { coachListJsonLdScripts } from "~/lib/shared/coach-list-head";
import { MIN_COACHES_FOR_SITEMAP } from "~/lib/shared/coach-listing-thresholds";
import { createBrandMeta } from "~/lib/shared/meta";

export const Route = createFileRoute(
  "/_public/coaches/subcategory/$subcategorySlug/",
)({
  loader: async ({ context, params }) => {
    const subcategorySlug = params.subcategorySlug;

    // Pre-load data into React Query and fetch for head function
    const [_coaches, subcategory, page] = await Promise.all([
      context.queryClient.ensureQueryData(
        coachQueryOptions.bySubcategory(subcategorySlug),
      ),
      context.queryClient.ensureQueryData({
        queryKey: ["subcategory", subcategorySlug],
        queryFn: () =>
          getSubcategoryBySlug({ data: { slug: subcategorySlug } }),
      }),
      context.queryClient.ensureQueryData({
        queryKey: ["subcategory", "page", subcategorySlug],
        queryFn: () => getSubcategoryPageContent({ data: { subcategorySlug } }),
      }),
    ]);

    return {
      subcategory,
      page,
      coachCount: _coaches.length,
    };
  },
  head: ({ loaderData, params }) => {
    const subcategorySlug = params.subcategorySlug;
    const pageUrl = `https://dreamcoachmatch.com/coaches/subcategory/${subcategorySlug}`;

    if (!loaderData) {
      return createBrandMeta({
        title: `${subcategorySlug} Coaches`,
        description: `Find specialized coaches in ${subcategorySlug} to help you achieve your personal and professional goals.`,
        canonical: pageUrl,
      });
    }

    const { subcategory, page, coachCount } = loaderData;
    const name =
      subcategory?.listingName || subcategory?.name || subcategorySlug;

    const title = page?.seoTitle ?? `${name} Coaches | Dream Coach Match`;
    const description =
      page?.metaDescription ??
      `Find specialized coaches in ${name} to help you achieve your personal and professional goals.`;

    const metaConfig = createBrandMeta({
      title,
      description,
      ogTitle: title,
      ogDescription: description,
      ogType: "website",
      ogUrl: pageUrl,
      twitterCard: "summary_large_image",
      canonical: pageUrl,
      // Thin subcategory pages stay out of the index (sitemap-aligned).
      noindexFollow: coachCount < MIN_COACHES_FOR_SITEMAP,
      author: "Dream Coach Match",
    });

    return {
      ...metaConfig,
      scripts: coachListJsonLdScripts({
        breadcrumbs: [
          { name: "Home", url: "https://dreamcoachmatch.com" },
          { name: "Coaches", url: "https://dreamcoachmatch.com/coaches" },
          {
            name: "Subcategories",
            url: "https://dreamcoachmatch.com/coaches/subcategory",
          },
          { name, url: pageUrl },
        ],
        faqItems: page?.faqItems,
      }),
    };
  },
  component: SubcategoryCoachesPage,
});

function SubcategoryCoachesPage() {
  const { subcategorySlug } = Route.useParams();

  const { data: coaches = [] } = useQuery(
    coachQueryOptions.bySubcategory(subcategorySlug),
  );

  const { data: subcategory } = useQuery({
    queryKey: ["subcategory", subcategorySlug],
    queryFn: () => getSubcategoryBySlug({ data: { slug: subcategorySlug } }),
  });

  const { data: page } = useQuery({
    queryKey: ["subcategory", "page", subcategorySlug],
    queryFn: () => getSubcategoryPageContent({ data: { subcategorySlug } }),
  });

  const name = subcategory?.listingName || subcategory?.name || "";

  const intro1 = `Discover coaches specializing in ${name}. Each brings unique skills and perspectives to help you succeed.`;
  const intro2 = `Browse profiles to find a ${name} coach whose approach aligns with your specific needs and goals.`;

  return (
    <div className="mx-auto max-w-7xl px-4 py-12">
      <Heading
        preHeadline="Dream Coach Match"
        headline={name ? `${name} Coaches` : "Coaches"}
        subheadline={
          name
            ? `Find specialized coaches in ${name} to help you achieve your personal and professional goals.`
            : "Find specialized coaches to help you achieve your personal and professional goals."
        }
        isH1
      />
      <BreakdownIntro
        breakdownName={name} // Will use listingName if available
        breakdownTypeSlug={subcategorySlug}
        introTextLine1={intro1}
        introTextLine2={intro2}
        ctaButtonTextTemplate="Find Your Coach"
        utmCampaignPrefix="subcategory_coaches"
      />
      <CoachListGrid
        className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-3"
        coaches={coaches}
        emptyMessage="No coaches found in this subcategory yet."
        midListCta={
          <MidListCTA
            breakdownName={name} // Will use listingName if available
            breakdownTypeSlug={subcategorySlug}
            preLinkText="Need help choosing?"
            ctaLinkTextTemplate="Let us match you with the ideal coach!"
            utmCampaignPrefix="subcategory_coaches_mid"
          />
        }
      />
      <BottomCTA
        itemCount={coaches.length}
        itemTypePlural="coaches"
        breakdownName={name} // Will use listingName if available
        breakdownTypeSlug={subcategorySlug}
        joinAsLinkUrl="/page/coach"
        joinAsLinkText="Join as a coach."
        mainCtaSubTextTemplate="Ready to connect with a specialized {} coach?"
        mainCtaButtonTextTemplate="Try the Matchmaker"
        utmCampaignPrefix="subcategory_coaches_bottom"
      />
      {page?.contentMd && (
        <div className="mx-auto prose mt-12">
          <MarkdownContent content={page.contentMd} demoteH1 />
        </div>
      )}
      {page?.faqItems && page.faqItems.length > 0 && (
        <FaqList items={page.faqItems} className="mx-auto max-w-2xl" />
      )}
    </div>
  );
}
